import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WorkIcon from '@mui/icons-material/Work';
import CampaignIcon from '@mui/icons-material/Campaign';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LogoutIcon from '@mui/icons-material/Logout';
import EmailIcon from '@mui/icons-material/Email';
import CloudIcon from '@mui/icons-material/Cloud';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Badge from '@mui/material/Badge';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 60;

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { collapsed, mobileOpen, isMobile, toggleCollapsed, closeMobile } = useSidebar();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        window.location = '/login';
    };

    const handleNav = (path) => {
        navigate(path);
        if (isMobile) closeMobile();
    };

    const isActive = (path) => location.pathname === path;

    const navItems = [
        { label: 'Pretraga uživo', path: '/main', icon: <SearchIcon />, show: true },
        { label: 'Alerti', path: '/alarms', icon: <NotificationsIcon />, show: user?.alert_limit > 0 },
        { label: 'Automatizirane pretrage', path: '/scheduledSearches', icon: <ScheduleIcon />, show: user?.scheduled_searches_limit > 0 },
        { label: 'Smart Brifinzi', path: '/briefings', icon: <AutoAwesomeIcon />, show: user?.briefing_limit > 0 },
        { label: 'Smart Alerti', path: '/smartAlerts', icon: (
            <Badge
                badgeContent={<AutoAwesomeIcon sx={{ fontSize: 10 }} />}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ '& .MuiBadge-badge': { minWidth: 14, height: 14, p: 0, bgcolor: 'transparent', color: 'inherit' } }}
            >
                <NotificationsActiveIcon />
            </Badge>
        ), show: user?.smart_alert_limit > 0 },
    ];

    const adminItems = [
        { label: 'Admin Dashboard', path: '/admin', icon: <DashboardIcon /> },
        { label: 'Korisnici', path: '/admin/users', icon: <PeopleIcon /> },
        { label: 'Scraper poslovi', path: '/admin/jobs', icon: <WorkIcon /> },
        { label: 'Aktiviranje alerta', path: '/admin/alert-firings', icon: <CampaignIcon /> },
        { label: 'Greške workera', path: '/admin/errors', icon: <ErrorIcon /> },
        { label: 'Ručno pokretanje', path: '/admin/triggers', icon: <PlayArrowIcon /> },
        { label: 'Email log', path: '/admin/emails', icon: <EmailIcon /> },
        { label: 'ScrapFly', path: '/admin/scrapfly', icon: <CloudIcon /> },
        { label: 'Smart Brifinzi', path: '/admin/briefings', icon: <AutoAwesomeIcon /> },
        { label: 'Smart Alerti', path: '/admin/smart-alerts', icon: <NotificationsActiveIcon /> },
    ];

    const width = collapsed && !isMobile ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    const showText = !collapsed || isMobile;

    const sidebarContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: showText ? 2 : 0, py: 1.5, justifyContent: showText ? 'space-between' : 'center' }}>
                {showText && (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0079bf', fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                        Media Scanner
                    </Typography>
                )}
                {!isMobile && (
                    <IconButton onClick={toggleCollapsed} size="small">
                        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                )}
            </Box>
            <Divider />

            {/* Nav items */}
            <List sx={{ px: 0.5, pt: 1 }}>
                {navItems.filter(i => i.show).map((item) => (
                    <Tooltip key={item.path} title={!showText ? item.label : ''} placement="right">
                        <ListItemButton
                            onClick={() => handleNav(item.path)}
                            sx={{
                                borderRadius: 1, mb: 0.5, minHeight: 44,
                                justifyContent: showText ? 'initial' : 'center',
                                backgroundColor: isActive(item.path) ? '#e3f2fd' : 'transparent',
                                '&:hover': { backgroundColor: isActive(item.path) ? '#e3f2fd' : '#f5f5f5' },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: showText ? 36 : 'auto', color: isActive(item.path) ? '#0079bf' : 'inherit' }}>
                                {item.icon}
                            </ListItemIcon>
                            {showText && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />}
                        </ListItemButton>
                    </Tooltip>
                ))}
            </List>

            {/* Admin section */}
            {user?.is_admin && (
                <>
                    <Divider sx={{ mx: 1, my: 0.5 }} />
                    <List sx={{ px: 0.5 }}>
                        {adminItems.map((item) => (
                            <Tooltip key={item.path} title={!showText ? item.label : ''} placement="right">
                                <ListItemButton
                                    onClick={() => handleNav(item.path)}
                                    sx={{
                                        borderRadius: 1, mb: 0.5, minHeight: 40,
                                        justifyContent: showText ? 'initial' : 'center',
                                        backgroundColor: isActive(item.path) ? '#e3f2fd' : 'transparent',
                                        '&:hover': { backgroundColor: isActive(item.path) ? '#e3f2fd' : '#f5f5f5' },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: showText ? 36 : 'auto', color: isActive(item.path) ? '#0079bf' : 'inherit' }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    {showText && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13 }} />}
                                </ListItemButton>
                            </Tooltip>
                        ))}
                    </List>
                </>
            )}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Bottom: user + logout */}
            <Divider />
            <Box sx={{ px: showText ? 2 : 0, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: showText ? 'space-between' : 'center' }}>
                {showText && (
                    <Typography variant="body2" sx={{ color: '#555', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {user?.username}
                    </Typography>
                )}
                <Tooltip title="Odjava" placement="right">
                    <IconButton onClick={handleLogout} size="small" sx={{ color: '#999' }}>
                        <LogoutIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={closeMobile}
                ModalProps={{ keepMounted: true }}
                sx={{ '& .MuiDrawer-paper': { width: EXPANDED_WIDTH, boxSizing: 'border-box', borderRight: '1px solid #e0e0e0' } }}
            >
                {sidebarContent}
            </Drawer>
        );
    }

    return (
        <Box
            sx={{
                width, flexShrink: 0,
                position: 'fixed', top: 0, left: 0, bottom: 0,
                backgroundColor: '#fff',
                borderRight: '1px solid #e0e0e0',
                transition: 'width 0.2s ease',
                zIndex: 1200,
                overflowX: 'hidden',
            }}
        >
            {sidebarContent}
        </Box>
    );
}
